import { useMemo } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { ProductFilterSortBar } from '../components/ProductFilterSortBar'
import { ProductCardGrid } from '../components/ProductCardGrid'
import { ProductGridSkeleton } from '../components/Skeleton'
import { SEARCH_DEFAULTS, SEARCH_UI } from '../constants/search'
import { resolveSearchSort } from '../helpers/search'
import { useProductSearch } from '../hooks/useProductSearch'
import '../styles/search-page.scss'

export function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const query = searchParams.get('q')?.trim() ?? ''
  const sortKey = searchParams.get('sort') ?? SEARCH_DEFAULTS.sortKey

  const activeSort = resolveSearchSort(sortKey)

  const { products, total, loading, loadingMore, error, hasMore, loadMore } =
    useProductSearch(query, activeSort.key)

  const title = useMemo(
    () => (query ? SEARCH_UI.resultsFor(query) : SEARCH_UI.pageTitle),
    [query],
  )

  function handleSortChange(nextSort: string) {
    const params = new URLSearchParams()
    if (query) params.set('q', query)
    if (nextSort && nextSort !== SEARCH_DEFAULTS.sortKey) {
      params.set('sort', nextSort)
    }
    setSearchParams(params, { replace: true })
  }

  return (
    <div className="search-page">
      <div className="container">
        <nav className="search-page__crumbs" aria-label="Breadcrumb">
          <Link to="/">{SEARCH_UI.homeCrumb}</Link>
          <span aria-hidden>›</span>
          <span aria-current="page">{SEARCH_UI.pageTitle}</span>
        </nav>

        <header className="search-page__head">
          <div className="search-page__title-block">
            <h1>{title}</h1>
          </div>
        </header>

        {query && !loading && !error && products.length > 0 && (
          <ProductFilterSortBar
            sort={activeSort.key}
            totalCount={total}
            onChange={handleSortChange}
          />
        )}

        {!query ? (
          <div className="search-page__empty">
            <p>{SEARCH_UI.emptyQueryTitle}</p>
            <p className="search-page__empty-hint">{SEARCH_UI.emptyQueryHint}</p>
          </div>
        ) : null}

        {query && loading ? <ProductGridSkeleton /> : null}
        {query && error ? (
          <p className="catalog-status catalog-status--error">{error}</p>
        ) : null}
        {query && !loading && !error && products.length === 0 ? (
          <p className="catalog-status">{SEARCH_UI.emptyResults}</p>
        ) : null}
        {query && !loading ? <ProductCardGrid products={products} /> : null}

        {query && hasMore ? (
          <div className="search-page__more">
            <button
              type="button"
              className="search-page__more-btn"
              onClick={loadMore}
              disabled={loadingMore}
            >
              {SEARCH_UI.loadMore}
            </button>
          </div>
        ) : null}
      </div>
    </div>
  )
}
